import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api';

// Create context for authentication state
const AuthContext = createContext();

/**
 * Auth Provider component to wrap the app
 * Provides authentication state and methods to all children
 */
export const AuthProvider = ({ children }) => {
  // State for user data and loading status
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Check if user is authenticated on app load
   * Restore user data from localStorage
   */
  useEffect(() => {
    const storedUser = localStorage.getItem('userData');
    const storedToken = localStorage.getItem('authToken');
    
    if (storedUser && storedToken) {
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  /**
   * Login user with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise} Login result
   */
  const login = async (email, password) => {
    try {
      setLoading(true);
      setError(null);
      
      // Call login API
      const response = await authAPI.login(email, password);
      const userData = response.data.data;
      
      // Store user data and token in localStorage
      localStorage.setItem('userData', JSON.stringify(userData));
      localStorage.setItem('authToken', userData.token);
      
      // Update state
      setUser(userData);
      setLoading(false);
      
      return { success: true, user: userData };
    } catch (error) {
      setLoading(false);
      
      // Extract error message from response
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.errors?.[0]?.msg ||
                          'Login failed. Please try again.';
      
      setError(errorMessage);
      
      return { 
        success: false, 
        error: errorMessage 
      };
    }
  };

  /**
   * Logout user - clear all stored data
   */
  const logout = () => {
    // Clear localStorage
    localStorage.removeItem('userData');
    localStorage.removeItem('authToken');
    
    // Reset state
    setUser(null);
    setError(null);
  };

  /**
   * Check if current user is an admin
   * @returns {boolean} True if user is admin
   */
  const isAdmin = () => user?.role === 'Admin';

  /**
   * Check if current user is an employee
   * @returns {boolean} True if user is employee
   */
  const isEmployee = () => user?.role === 'Employee';

  /**
   * Check if user is authenticated
   * @returns {boolean} True if user is logged in
   */
  const isAuthenticated = () => !!user;

  // Context value to be provided to children
  const value = {
    user,
    loading,
    error,
    login,
    logout,
    isAdmin,
    isEmployee,
    isAuthenticated,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Custom hook to use auth context
 * @returns {Object} Auth context value
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;